import React, { useMemo } from 'react';
import { CgFilterSelect } from 'modules/Common/ui/CgFilterDropdown';
import { useTranslation } from 'react-i18next';
import {
  countryDisplayName,
  countrySearchCodes,
  countrySearchText,
  compareLocalized,
  foldSearch,
} from 'helpers/countries';
import { useLocaleStore } from 'stores/useLocaleStore';
import {
  CountryDropdown,
  isNullCountry,
  useAllCountries,
  useEUCountries,
  useCbamCountries,
} from 'modules/Common/utils/Utility';

// Searchable country select. `scope` selects which list to load, mirroring
// FormControlTypes.{ALL,EU,CBAM}Dropdown from v1 CgForm.
//
// Rendered with CgFilterSelect (Radix popover) rather than react-select:
// react-select portals its menu to document.body, which sits outside a Radix
// CgDialog's layer — clicks on the menu registered as outside-interactions
// and the dropdown misbehaved inside the supplier/installation modals.
// CgFilterSelect is dialog-portal-aware (useCgDialogPortalContainer), so the
// menu stays inside the dialog's layer.
//
// `valueAs` controls what gets stored in the form state:
//   'id'   — country._id (matches the legacy CBAMDropdown / number-or-string
//            shape used by OperatorInstallation address.country)
//   'iso2' — country.iso2 (used by InstallationCreateForm, declarant side)
//
// `excludeIso2` drops countries from the list (e.g. ['GB'] for a UK CBAM
// installation, which cannot be in the UK).
// Match against the folded label + English/native names + aliases, not just
// the CLDR label: CLDR calls TR "Türkiye", so "Turkey" (our data's name) and
// "Turkiye" (no umlaut) would otherwise find nothing. A query of two letters
// or fewer is a code: it must equal the iso2 or an alias ("us" → US, never
// Russia or Australia; "uk" → GB).
export const filterCountryOption = (option, input) => {
  const q = foldSearch(input).trim();
  if (!q) return true;
  if (q.length <= 2) return (option.data?.searchCodes ?? []).includes(q);
  return (option.data?.searchText ?? foldSearch(option.label)).includes(q);
};

export default function CountrySelectField({
  field,
  fieldState,
  id,
  scope = CountryDropdown.ALL,
  valueAs = 'id',
  placeholder,
  disabled,
  excludeIso2,
}) {
  const { t } = useTranslation('common');
  const locale = useLocaleStore((s) => s.locale);
  const all = useAllCountries();
  const eu = useEUCountries();
  const cbam = useCbamCountries();

  const list = scope === CountryDropdown.EU ? eu : scope === CountryDropdown.CBAM ? cbam : all;

  // Labels come from CLDR via Intl.DisplayNames, and the list is re-sorted
  // with Intl.Collator for the active language — a list ordered by the English
  // names is in visibly random order once the labels read "Alemania" (A in
  // Spanish) and "Germania" (G in Italian).
  const options = useMemo(
    () =>
      (list || [])
        .filter((c) => !excludeIso2?.includes(c.iso2))
        .map((c) => ({
          value: valueAs === 'iso2' ? c.iso2 : String(c._id),
          label: countryDisplayName(c, locale),
          searchText: countrySearchText(c, locale),
          searchCodes: countrySearchCodes(c),
        }))
        .sort((a, b) => compareLocalized(a.label, b.label, locale)),
    [list, valueAs, locale, excludeIso2],
  );

  // Treat the -1 "country is null" sentinel as an empty selection so the
  // placeholder shows instead of a synthesized "-1" option. We only normalize
  // what's displayed — selecting a real country still emits the real value.
  // Values are stored as strings, so coerce before matching options.
  const value =
    field.value === undefined || field.value === null || isNullCountry(field.value)
      ? ''
      : String(field.value);

  return (
    <CgFilterSelect
      id={id}
      options={options}
      value={value}
      onChange={(v) => field.onChange(v ?? '')}
      placeholder={placeholder ?? t('fields.country.placeholder', 'Please select a country')}
      disabled={disabled}
      error={!!fieldState?.error}
      searchable
      filterOption={filterCountryOption}
    />
  );
}
